import React, { useState, useEffect, useRef } from 'react';
import Icon from './AppIcon';
import { 
  TIMEZONE_DATA, 
  getTimezonesByRegion, 
  searchTimezones, 
  getPopularTimezones,
  getTimezoneDetails 
} from '../utils/timezones';

const TimezoneSelector = ({ 
  value, 
  onChange, 
  label, 
  required = false, 
  error = '',
  className = '' 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTimezones, setFilteredTimezones] = useState(TIMEZONE_DATA);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [showPopular, setShowPopular] = useState(true);
  
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);
  const listRef = useRef(null);

  // Get selected timezone details
  const selectedTimezone = value ? getTimezoneDetails(value) : null;

  // Handle search and filtering
  useEffect(() => {
    if (searchQuery?.trim() === '') {
      setFilteredTimezones(TIMEZONE_DATA);
      setShowPopular(true);
    } else {
      const results = searchTimezones(searchQuery);
      setFilteredTimezones(results);
      setShowPopular(false);
      setHighlightedIndex(0);
    }
  }, [searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
      }
    };

    if (isOpen) {
      document?.addEventListener('mousedown', handleClickOutside);
      return () => document?.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      searchInputRef?.current?.focus();
    }
  }, [isOpen]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightedIndex >= 0 && listRef?.current) {
      const highlightedElement = listRef?.current?.children?.[highlightedIndex];
      if (highlightedElement) {
        highlightedElement?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [highlightedIndex]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchQuery('');
      setHighlightedIndex(-1);
    }
  };

  const handleSelect = (timezone) => {
    onChange?.(timezone?.value);
    setIsOpen(false);
    setSearchQuery('');
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e?.key === 'Enter' || e?.key === ' ' || e?.key === 'ArrowDown') {
        e?.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    const displayList = showPopular ? getPopularTimezones() : filteredTimezones;

    switch (e?.key) {
      case 'ArrowDown':
        e?.preventDefault();
        setHighlightedIndex(prev => 
          prev < displayList?.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e?.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e?.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < displayList?.length) {
          handleSelect(displayList?.[highlightedIndex]);
        }
        break;
      case 'Escape':
        e?.preventDefault();
        setIsOpen(false);
        setSearchQuery('');
        setHighlightedIndex(-1);
        break;
      default:
        break;
    }
  };

  const renderTimezoneOption = (timezone, index, isHighlighted) => (
    <div
      key={timezone?.value}
      onClick={() => handleSelect(timezone)}
      className={`
        px-4 py-2.5 cursor-pointer transition-colors
        ${isHighlighted ? 'bg-primary/10' : 'hover:bg-gray-50'}
        ${value === timezone?.value ? 'bg-primary/5 border-l-2 border-primary' : ''}
      `}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900 truncate">
              {timezone?.label}
            </span>
            {value === timezone?.value && (
              <Icon name="Check" size={16} className="text-primary flex-shrink-0" />
            )}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {timezone?.value}
          </div>
        </div>
        <div className="text-sm font-mono text-gray-600 flex-shrink-0">
          {timezone?.offset}
        </div>
      </div>
    </div>
  );

  const renderGroupedTimezones = () => {
    const grouped = getTimezonesByRegion();
    const regions = Object?.keys(grouped)?.sort();

    return regions?.map(region => {
      const regionTimezones = grouped?.[region]?.filter(tz => 
        filteredTimezones?.some(filtered => filtered?.value === tz?.value)
      );

      if (regionTimezones?.length === 0) return null;

      return (
        <div key={region}>
          <div className="px-4 py-2 bg-gray-100 border-b border-gray-200">
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              {region}
            </span>
          </div>
          {regionTimezones?.map((tz, idx) => 
            renderTimezoneOption(tz, idx, false)
          )}
        </div>
      );
    });
  };

  const renderPopularTimezones = () => {
    const popular = getPopularTimezones();
    
    return (
      <>
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
          <div className="flex items-center gap-2">
            <Icon name="Star" size={14} className="text-blue-600" />
            <span className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
              Popular Timezones
            </span>
          </div>
        </div>
        {popular?.map((tz, idx) => 
          renderTimezoneOption(tz, idx, idx === highlightedIndex)
        )}
        <div className="px-4 py-2 bg-gray-50 border-t border-gray-200">
          <button
            onClick={() => setShowPopular(false)}
            className="text-sm text-primary hover:text-primary/80 font-medium flex items-center gap-1"
          >
            View all timezones
            <Icon name="ChevronRight" size={14} />
          </button>
        </div>
      </>
    );
  };

  return (
    <div className="space-y-1" ref={dropdownRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {/* Selected Value Display */}
        <button
          type="button"
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          className={`
            w-full px-4 py-2.5 text-left bg-white border rounded-lg
            focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
            transition-colors flex items-center justify-between gap-2
            ${error ? 'border-red-500' : 'border-gray-300 hover:border-gray-400'}
            ${className}
          `}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <div className="flex-1 min-w-0">
            {selectedTimezone ? (
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900 truncate">
                  {selectedTimezone?.label}
                </span>
                <span className="text-sm font-mono text-gray-500 flex-shrink-0">
                  ({selectedTimezone?.offset})
                </span>
              </div>
            ) : (
              <span className="text-gray-400">Select timezone...</span>
            )}
          </div>
          <Icon 
            name={isOpen ? "ChevronUp" : "ChevronDown"} 
            size={20} 
            className="text-gray-400 flex-shrink-0" 
          />
        </button>

        {/* Dropdown Panel */}
        {isOpen && (
          <div className="absolute z-50 w-full mt-2 bg-white border border-gray-300 rounded-lg shadow-xl max-h-96 overflow-hidden">
            {/* Search Input */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-3">
              <div className="relative">
                <Icon 
                  name="Search" 
                  size={18} 
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" 
                />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e?.target?.value)}
                  placeholder="Search timezones, cities, or UTC offset..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-sm"
                  onKeyDown={handleKeyDown}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <Icon name="X" size={16} />
                  </button>
                )}
              </div>
            </div>

            {/* Results */}
            <div className="overflow-y-auto max-h-80" ref={listRef}>
              {filteredTimezones?.length === 0 ? (
                <div className="px-4 py-8 text-center">
                  <Icon name="Search" size={32} className="mx-auto text-gray-300 mb-2" />
                  <p className="text-sm text-gray-500">No timezones found</p>
                  <p className="text-xs text-gray-400 mt-1">Try a different search term</p>
                </div>
              ) : showPopular && searchQuery?.trim() === '' ? (
                renderPopularTimezones()
              ) : (
                renderGroupedTimezones()
              )}
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="flex items-start gap-1.5 text-red-600">
          <Icon name="AlertCircle" size={14} className="mt-0.5 flex-shrink-0" />
          <p className="text-xs">{error}</p>
        </div>
      )}
    </div>
  );
};

export default TimezoneSelector;